import { omit } from "lodash";

import assignObjectValuesToSet from "utils/assignObjectValuesToSet";

const simpleEntityMapping = {
  Client: { profileKey: "profile", lawyerKey: "lawyer" },
  Ex: { profileKey: "exProfile", lawyerKey: "exLawyer" },
};

const arrayEntityMapping = {
  Child: { arrayKey: "children" },
  Party: { arrayKey: "parties" },
};

const getSimpleEntityUpdate = ({
  entityObject,
  upsertLawyerValues,
  isUpdateSelfRepresented,
  isSelfRepresented,
  profileKey,
  lawyerKey,
}) => {
  if (isUpdateSelfRepresented) {
    if (!entityObject?.lawyer?.id) {
      return {
        [profileKey]: {
          update: {
            data: {
              isSelfRepresented: { set: isSelfRepresented },
            },
          },
        },
      };
    }

    return {
      [profileKey]: {
        update: {
          data: {
            isSelfRepresented: { set: isSelfRepresented },
          },
        },
      },
      [lawyerKey]: {
        delete: {
          id: { equals: entityObject.lawyer.id },
        },
      },
    };
  }

  return {
    [lawyerKey]: upsertLawyerValues,
  };
};

const getArrayEntityUpdate = ({
  entityObject,
  upsertLawyerValues,
  isUpdateSelfRepresented,
  isSelfRepresented,
  arrayKey,
}) => {
  if (isUpdateSelfRepresented) {
    if (!entityObject?.lawyer?.id) {
      return {
        [arrayKey]: {
          update: {
            where: { id: entityObject.id },
            data: {
              isSelfRepresented: { set: isSelfRepresented },
            },
          },
        },
      };
    }
    return {
      [arrayKey]: {
        update: {
          where: { id: entityObject.id },
          data: {
            isSelfRepresented: { set: isSelfRepresented },
            lawyer: {
              delete: {
                id: { equals: entityObject.lawyer.id },
              },
            },
          },
        },
      },
    };
  }

  return {
    [arrayKey]: {
      update: {
        where: { id: entityObject.id },
        data: {
          lawyer: upsertLawyerValues,
        },
      },
    },
  };
};

const getGqlObjectByEntityType = ({
  entityType,
  entityObject,
  upsertLawyerValues,
  isUpdateSelfRepresented,
  isSelfRepresented,
}) => {
  if (simpleEntityMapping[entityType]) {
    const { profileKey, lawyerKey } = simpleEntityMapping[entityType];
    return getSimpleEntityUpdate({
      entityObject,
      upsertLawyerValues,
      isUpdateSelfRepresented,
      isSelfRepresented,
      profileKey,
      lawyerKey,
    });
  }
  if (arrayEntityMapping[entityType]) {
    const { arrayKey } = arrayEntityMapping[entityType];
    return getArrayEntityUpdate({
      entityObject,
      upsertLawyerValues,
      isUpdateSelfRepresented,
      isSelfRepresented,
      arrayKey,
    });
  }

  return {};
};

export const prepareVariablesForUpdateLawyer = ({
  values,
  entityObject,
  entityType,
  user,
  hasIsSelfRepresented,
}) => {
  if (hasIsSelfRepresented && entityObject.isSelfRepresented !== values.isSelfRepresented) {
    const variables = {
      where: {
        id: user?.client?.id,
      },
      data: getGqlObjectByEntityType({
        entityObject,
        entityType,
        isUpdateSelfRepresented: true,
        isSelfRepresented: values.isSelfRepresented,
      }),
    };

    return { variables };
  }

  const location = {
    phone: values.location.phone,
    postal: values.location.postal,
    country: values.location.country,
    residence: values.location.residence,
    street1: values.location.street1,
    street2: values.location.street2,
    city: values.location.city,
    email: values.location.email,
    fax: values.location.fax,
  };

  const upsertLawyerValues = {
    upsert: {
      create: {
        name: values.name,
        orgName: values.orgName,
        sanityId: values.sanityId || "non-sanity",
        location: {
          create: location,
        },
      },
      update: {
        ...assignObjectValuesToSet(
          omit(
            { ...values, sanityId: values.sanityId || "non-sanity" },
            "location",
            "isSelfRepresented",
          ),
        ),
        location: {
          upsert: {
            create: location,
            update: assignObjectValuesToSet(location),
          },
        },
      },
    },
  };

  const variables = {
    where: {
      id: user?.client?.id,
    },
    data: getGqlObjectByEntityType({
      entityObject,
      entityType,
      upsertLawyerValues,
    }),
  };

  return { variables };
};
